import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTabsStore, frozenTabIds } from './tabsStore';
import { tabLabel } from './model/types';
import { ScreenHost } from '@/screens/ScreenHost';
import { ErrorBoundary } from '@/shared/ui/ErrorBoundary';
import { HomePage } from '@/features/home/HomePage';
import { TabPaneContext } from '@/shared/ui/tabPane';
import { BusyOverlay } from '@/shared/ui/BusyOverlay';

const RATIO_KEY = 'octo.web.tabs.splitRatio';
const MIN_RATIO = 0.2;
const MAX_RATIO = 0.8;

function readRatio(): number {
  try {
    const raw = Number(localStorage.getItem(RATIO_KEY));
    return Number.isFinite(raw) && raw >= MIN_RATIO && raw <= MAX_RATIO ? raw : 0.5;
  } catch {
    return 0.5;
  }
}

/**
 * Keep-alive pane'ler: tüm sekmeler mount kalır, pasif olan hidden+inert. En eski pasifler
 * dondurulur (unmount).
 *
 * Yan yana görünümde pane'ler DOM'da yer değiştirmez, yalnızca konumları değişir. Taşımak
 * (sol/sağ için ayrı konteyner) React'te unmount/remount demek olurdu ve ekranın durumu
 * —filtreler, seçili satır, düzenlenen form— her açılışta sıfırlanırdı.
 */
export function TabPanes() {
  const { t } = useTranslation();
  const tabs = useTabsStore((s) => s.tabs);
  const activeTabId = useTabsStore((s) => s.activeTabId);
  const splitTabId = useTabsStore((s) => s.splitTabId);
  const setSplit = useTabsStore((s) => s.setSplit);
  // Seçici her render'da yeni Set döndürmemeli (sonsuz döngü) → useMemo ile türet
  const frozen = useMemo(() => frozenTabIds(tabs, activeTabId, splitTabId), [tabs, activeTabId, splitTabId]);
  /*
   * Bu oturumda en az bir kez görünen sekmeler. Sayfa yenilenince geri yüklenen sekmelerin
   * HEPSİ (12'ye kadar) aynı anda mount olup ekran paketini, verisini, detaylarını çekiyordu;
   * SP ekranları parametre penceresini öndeki sekmenin üstünde açıyordu. Artık sekme ilk
   * gösterildiğinde mount olur, sonra keep-alive kalır.
   */
  const [visited, setVisited] = useState<ReadonlySet<string>>(
    () => new Set([activeTabId, splitTabId].filter((x): x is string => !!x)),
  );
  // Render sırasında güncellenir (efekt değil): yeni öne gelen sekme boş bir kare görünmeden mount olur.
  const unseen = [activeTabId, splitTabId].filter((x): x is string => !!x && !visited.has(x));
  if (unseen.length) setVisited(new Set([...visited, ...unseen]));

  const hostRef = useRef<HTMLDivElement>(null);
  const [ratio, setRatio] = useState(readRatio);
  const split = splitTabId ? tabs.find((t) => t.tabId === splitTabId) : undefined;

  const startResize = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    const box = hostRef.current?.getBoundingClientRect();
    if (!box) return;
    const move = (ev: PointerEvent) => {
      const next = Math.min(MAX_RATIO, Math.max(MIN_RATIO, (ev.clientX - box.left) / box.width));
      setRatio(next);
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(RATIO_KEY, String(ratio));
    } catch {
      /* depolama yoksa yalnızca bu oturumda geçerli */
    }
  }, [ratio]);

  /** Pane'in yan yana görünümdeki yeri; split yokken tüm alanı kaplar. */
  const paneStyle = (which: 'main' | 'side'): CSSProperties => {
    if (!split) return { left: 0, right: 0 };
    return which === 'main' ? { left: 0, width: `${ratio * 100}%` } : { left: `${ratio * 100}%`, right: 0 };
  };

  const homeActive = activeTabId === null;

  return (
    <div ref={hostRef} className="relative min-h-0 flex-1">
      <div
        hidden={!homeActive}
        inert={!homeActive}
        className="absolute inset-y-0 overflow-auto"
        style={paneStyle('main')}
      >
        <HomePage />
      </div>
      {tabs.map((tab) => {
        const isSide = tab.tabId === splitTabId;
        const isMain = tab.tabId === activeTabId;
        const visible = isMain || isSide;
        if (!visible && (frozen.has(tab.tabId) || !visited.has(tab.tabId))) return null;
        return (
          <div
            key={tab.tabId}
            hidden={!visible}
            inert={!visible}
            className="absolute inset-y-0 flex flex-col overflow-hidden"
            style={paneStyle(isSide ? 'side' : 'main')}
          >
            {/* Yan panelin kendi başlığı var: hangi sekme olduğunu ve nasıl kapatılacağını
                göstermek gerekiyor, şeritteki işaret tek başına yetmiyor. */}
            {isSide ? (
              <div className="border-border bg-bg-muted text-fg-muted flex h-7 shrink-0 items-center gap-2 border-b px-2 text-xs">
                <span className="truncate">{tabLabel(tab)}</span>
                <button
                  type="button"
                  aria-label={t('shell.tab.splitOff')}
                  title={t('shell.tab.splitOff')}
                  onClick={() => setSplit(null)}
                  className="hover:bg-bg-hover hover:text-fg ml-auto rounded p-0.5"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : null}
            <ErrorBoundary>
              <TabPaneContext value={{ tabId: tab.tabId, visible }}>
                <ScreenHost tab={tab} active={visible} />
              </TabPaneContext>
            </ErrorBoundary>
            {/* Bu sekmede süren uzun işlem (toplu silme, SP hazırlığı…) yalnız bu sekmeyi örter. */}
            <BusyOverlay scope={tab.tabId} />
          </div>
        );
      })}
      {split ? (
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label={t('shell.tab.splitResize')}
          onPointerDown={startResize}
          className="bg-border hover:bg-brand-500 absolute inset-y-0 z-10 w-1 cursor-col-resize"
          style={{ left: `calc(${ratio * 100}% - 2px)` }}
        />
      ) : null}
    </div>
  );
}
